import { MARKETING_WEBSITE } from 'constants/links';
import type { EventStatus } from 'enum/event-status';

export const Routes = {
  external: {
    marketing: {
      about: () => `${MARKETING_WEBSITE}`,
      privacy: () => `${MARKETING_WEBSITE}/privacy`,
      terms: () => `${MARKETING_WEBSITE}/terms`,
      contact: () => `${MARKETING_WEBSITE}/contact`,
    },
  },

  home: () => '/',
  myEvents: () => '/events',
  organizations: (schoolId: string) => `/organizations?schoolId=${schoolId}`,
  eventTickets: () => '/events/tickets',
  createEvent: (fromEventId?: string) =>
    !fromEventId ? '/events/new' : `/events/new?from=${fromEventId}`,
  help: () => '/help',
  explore: (schoolSlug?: string) =>
    schoolSlug ? `/${schoolSlug}/events` : '/explore',
  editEvent: (id: string) => `/events/${id}`,
  reserveTickets: (eventId: string) => `/tickets/${eventId}`,
  viewReceipt: (receiptId: string) => `/receipt/${receiptId}`,
  viewTicket: (receiptId: string) => `/ticket/${receiptId}`,
  eventSelfCheckIn: (eventId: string) => `/events/${eventId}/check-in`,
  eventFeedback: (eventId: string) => `?feedback=${eventId}`,

  error404: '/404',

  settings: () => '/settings',
  profile: () => '/settings/account',
  manageEmailAddresses: () => '/settings/email-addresses',
  communicationPreferences: () => '/settings/communication',
  passwordAndSecurity: () => '/settings/security',
  schoolConnections: () => '/settings/schools',
  payments: () => '/settings/payments',

  signUp: () => '/signup',
  signUpConfirm: () => '/signup-confirm',
  logIn: (next?: string) => {
    if (next && next.startsWith('/') && !next.startsWith('//') && !next.startsWith('/login')) {
      return `/login?next=${encodeURIComponent(next)}`;
    }
    return '/login';
  },
  resetPassword: () => '/reset-password',

  download: () => '/download',

  search: (query: string) => {
    return `/search?q=${encodeURIComponent(query)}`;
  },

  admin: {
    default: () => Routes.admin.organizations(),
    organizations: () => '/admin/organizations',
    users: () => '/admin/users',
    events: () => '/admin/events',
    eventRequests: () => '/admin/events/requests',
    eventAttendees: (id: string) => `/admin/events/${id}/attendees`,
    tickets: () => '/admin/tickets',
    details: () => '/admin/details',
    help: () => '/admin/help',
    analytics: () => '/admin/analytics',
    createOrganization: () => '/admin/organization/new',
    viewOrganization: (id: string) => `/admin/organization/${id}`,
    editOrganization: (id: string) => `/admin/organization/${id}/edit`,
    eventCheckIn: (id: string) => `/admin/events/${id}/check-in`,
    eventDetail: (id: string) => `/admin/events/${id}/detail`,
    eventRequest: (id: string) => `/admin/events/${id}/request`,
    schedule: () => '/admin/schedule',
    saml: () => '/admin/saml',
    permissionRequests: () => '/admin/permission-requests',
    organizationDetail: (id: string) => `/admin/organization/detail/${id}`,
    pathways: () => '/admin/pathways',
    createPathway: () => '/admin/pathways/new',
    pathwayDetail: (id: string) => `/admin/pathways/${id}`,
  },

  master: {
    default: () => Routes.master.schools(),
    majors: () => '/master/majors',
    schools: () => '/master/schools',
    school: (id: string) => `/master/school/${id}`,
    createSchool: () => '/master/school/new',
    addUser: (schoolId: string) => `/master/school/${schoolId}/users/new`,
    organizations: () => '/master/organizations',
    users: () => '/master/users',
    events: () => '/master/events',
    tickets: () => '/master/tickets',
    configurations: () => '/master/configurations',
    samlConfig: (schoolId: string) => `/master/saml/${schoolId}`,
  },
} as const;

/** Pending events use the request/review layout; others use the standard admin detail page. */
export const adminEventReviewUrl = (id: string, status: EventStatus, tab?: 'approvals'): string => {
  const url = status === 'PENDING_APPROVAL' ? Routes.admin.eventRequest(id) : Routes.admin.eventDetail(id);
  return tab ? `${url}?tab=${tab}` : url;
};
